import React, { useState, useEffect } from 'react';
import { IonIcon, IonFab, IonFabButton, IonRefresher, IonRefresherContent, RefresherEventDetail } from '@ionic/react';
import { add, trash, calendar, heart, close } from 'ionicons/icons';
import { format } from 'date-fns';
import {
  getCustomQuotes, addCustomQuote, deleteCustomQuote,
  getInspirationHistory, getPresetQuotes,
  type CustomQuote, type PresetCategory,
} from '../services/inspiration';
import InspirationWidget from '../components/InspirationWidget';

// UI Library imports
import { PageShell } from '../components/ui/PageShell';
import { Button } from '../components/ui/Button';
import { Modal, ModalSection } from '../components/ui/Modal';
import { Search } from '../components/ui/Search';
import { Segment, type SegmentOption } from '../components/ui/Segment';
import { Badge } from '../components/ui/Badge';
import { TextInput } from '../components/ui/TextInput';
import { Card } from '../components/ui/Card';
import { List, type ListItemProps } from '../components/ui/List';

import './QuoteManager.css';

interface QuoteManagerProps {
  householdId: string;
  currentUserId?: string;
}

type ViewTab = 'today' | 'custom' | 'presets' | 'history';

// View tab options for Segment
const VIEW_TAB_OPTIONS: SegmentOption<ViewTab>[] = [
  { value: 'today', label: 'Today' },
  { value: 'custom', label: 'My Quotes' },
  { value: 'presets', label: 'Browse' },
  { value: 'history', label: 'History' },
];

// Preset category options for Segment
const PRESET_CATEGORY_OPTIONS: SegmentOption<PresetCategory>[] = [
  { value: 'motivational', label: '💪 Motivational' },
  { value: 'kids', label: '🧒 Kids' },
  { value: 'gratitude', label: '🙏 Gratitude' },
  { value: 'wisdom', label: '🦉 Wisdom' },
  { value: 'funny', label: '😄 Funny' },
];

const QuoteManager: React.FC<QuoteManagerProps> = ({ householdId, currentUserId }) => {
  const [activeTab, setActiveTab] = useState<ViewTab>('today');
  const [customQuotes, setCustomQuotes] = useState<CustomQuote[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [presetCategory, setPresetCategory] = useState<PresetCategory>('motivational');
  const [presetQuotes, setPresetQuotes] = useState<any[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchText, setSearchText] = useState('');

  // New quote form
  const [newQuote, setNewQuote] = useState({
    text: '',
    author: '',
    showOnDate: '',
    tags: [] as string[],
  });
  const [newTag, setNewTag] = useState('');

  // Load data
  useEffect(() => {
    const loadData = async () => {
      const [quotes, hist] = await Promise.all([
        getCustomQuotes(householdId),
        getInspirationHistory(householdId, 30),
      ]);
      setCustomQuotes(quotes);
      setHistory(hist);
    };
    loadData();
  }, [householdId]);

  // Load presets when category changes
  useEffect(() => {
    getPresetQuotes(presetCategory).then(setPresetQuotes);
  }, [presetCategory]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    const [quotes, hist] = await Promise.all([
      getCustomQuotes(householdId),
      getInspirationHistory(householdId, 30),
    ]);
    setCustomQuotes(quotes);
    setHistory(hist);
    event.detail.complete();
  };

  const handleAddQuote = async () => {
    if (!newQuote.text.trim()) return;

    await addCustomQuote(householdId, newQuote.text, {
      author: newQuote.author || undefined,
      addedBy: currentUserId,
      tags: newQuote.tags.length > 0 ? newQuote.tags : undefined,
      showOnDate: newQuote.showOnDate || undefined,
    });

    // Refresh list
    const quotes = await getCustomQuotes(householdId);
    setCustomQuotes(quotes);

    // Reset form
    setNewQuote({ text: '', author: '', showOnDate: '', tags: [] });
    setShowAddModal(false);
  };

  const handleDeleteQuote = async (quoteId: string) => {
    await deleteCustomQuote(quoteId);
    setCustomQuotes(prev => prev.filter(q => q.id !== quoteId));
  };

  const addTagHandler = () => {
    if (!newTag.trim() || newQuote.tags.includes(newTag.trim())) return;
    setNewQuote(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
    setNewTag('');
  };

  const removeTag = (tag: string) => {
    setNewQuote(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const filteredQuotes = customQuotes.filter(q =>
    q.quote_text.toLowerCase().includes(searchText.toLowerCase()) ||
    q.author?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Convert custom quotes to List items
  const customQuoteItems: ListItemProps[] = filteredQuotes.map(quote => ({
    id: quote.id,
    label: `"${quote.quote_text}"`,
    description: quote.author ? `— ${quote.author}` : undefined,
    end: {
      element: (
        <Button
          fill="clear"
          size="small"
          iconStart={trash}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteQuote(quote.id);
          }}
          aria-label="Delete quote"
          style={{ color: 'var(--ion-color-danger)' }}
        />
      ),
    },
    meta: (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {quote.show_on_date && (
          <Badge variant="primary" size="sm">
            <IonIcon icon={calendar} style={{ marginRight: 4 }} />
            {format(new Date(quote.show_on_date), 'MMM d')}
          </Badge>
        )}
        {quote.tags?.map(tag => (
          <Badge key={tag} variant="secondary" size="sm">{tag}</Badge>
        ))}
        <span style={{
          fontSize: 11,
          color: 'var(--ion-color-medium)',
          fontFamily: 'var(--kff-data-font)',
        }}>
          Shown {quote.times_shown}x
        </span>
      </div>
    ),
  }));

  // Convert preset quotes to List items
  const presetQuoteItems: ListItemProps[] = presetQuotes.map((quote, index) => ({
    id: `preset-${index}`,
    label: `"${quote.quote_text}"`,
    description: quote.author ? `— ${quote.author}` : undefined,
    end: {
      element: (
        <Button
          fill="clear"
          size="small"
          iconStart={heart}
          onClick={(e) => {
            e.stopPropagation();
            setNewQuote({
              text: quote.quote_text,
              author: quote.author || '',
              showOnDate: '',
              tags: [],
            });
            setShowAddModal(true);
          }}
          aria-label="Add to my quotes"
        />
      ),
    },
  }));

  // Convert history to List items
  const historyItems: ListItemProps[] = history.map((entry, index) => ({
    id: `history-${index}`,
    label: `"${entry.quote_text}"`,
    description: entry.author ? `— ${entry.author}` : undefined,
    start: {
      element: (
        <Badge variant="secondary" size="sm">
          {format(new Date(entry.shown_date), 'M/d')}
        </Badge>
      ),
    },
    end: {
      element: (
        <Badge variant="neutral" fill="outline" size="sm">{entry.source_name}</Badge>
      ),
    },
  }));

  return (
    <PageShell
      title="Daily Inspiration"
      headerType="page"
      showBack
      renderBeforeContent={
        <div style={{ padding: '0 16px 12px' }}>
          <Segment
            options={VIEW_TAB_OPTIONS}
            value={activeTab}
            onChange={setActiveTab}
            size="sm"
          />
        </div>
      }
    >
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      {/* TODAY TAB */}
      {activeTab === 'today' && (
        <div className="today-view">
          <InspirationWidget
            householdId={householdId}
            style="card"
            showRefresh={true}
            showShare={true}
          />

          <Card style={{ marginTop: 16 }}>
            <p style={{
              color: 'var(--ion-color-medium)',
              fontFamily: 'var(--kff-data-font)',
              fontSize: 14,
              lineHeight: 1.5,
              marginBottom: 16,
            }}>
              💡 <strong>Tip:</strong> Add your own family quotes, favorite sayings,
              or schedule special messages for birthdays and anniversaries!
            </p>
            <Button
              variant="primary"
              expand="block"
              iconStart={add}
              onClick={() => setShowAddModal(true)}
            >
              Add Family Quote
            </Button>
          </Card>
        </div>
      )}

      {/* CUSTOM QUOTES TAB */}
      {activeTab === 'custom' && (
        <>
          <div style={{ padding: '0 16px 16px' }}>
            <Search
              value={searchText}
              onChange={setSearchText}
              placeholder="Search quotes..."
              variant="filled"
              size="md"
            />
          </div>

          {customQuoteItems.length === 0 ? (
            <Card style={{ margin: 16, textAlign: 'center' }}>
              <div style={{ padding: '24px 16px' }}>
                <p style={{
                  fontFamily: 'var(--kff-data-font)',
                  color: 'var(--ion-color-medium)',
                  marginBottom: 8,
                }}>
                  No custom quotes yet
                </p>
                <p style={{
                  fontFamily: 'var(--kff-data-font)',
                  color: 'var(--ion-color-medium)',
                  fontSize: 14,
                }}>
                  Add your family's favorite sayings!
                </p>
              </div>
            </Card>
          ) : (
            <List
              items={customQuoteItems}
              variant="default"
              size="md"
              lines="inset"
            />
          )}

          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton onClick={() => setShowAddModal(true)}>
              <IonIcon icon={add} />
            </IonFabButton>
          </IonFab>
        </>
      )}

      {/* PRESETS TAB */}
      {activeTab === 'presets' && (
        <>
          <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
            <Segment
              options={PRESET_CATEGORY_OPTIONS}
              value={presetCategory}
              onChange={setPresetCategory}
              size="sm"
            />
          </div>

          <List
            items={presetQuoteItems}
            variant="default"
            size="md"
            lines="inset"
          />
        </>
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <>
          <div style={{
            padding: '12px 16px',
            fontFamily: 'var(--kff-heading-font)',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--ion-color-medium)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '1px solid var(--ion-border-color)',
          }}>
            Last 30 Days
          </div>

          {historyItems.length === 0 ? (
            <Card style={{ margin: 16, textAlign: 'center' }}>
              <div style={{ padding: '24px 16px' }}>
                <p style={{
                  fontFamily: 'var(--kff-data-font)',
                  color: 'var(--ion-color-medium)',
                }}>
                  No history yet
                </p>
              </div>
            </Card>
          ) : (
            <List
              items={historyItems}
              variant="default"
              size="md"
              lines="inset"
            />
          )}
        </>
      )}

      {/* ADD QUOTE MODAL */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        size="lg"
        header={{ title: 'Add Quote' }}
        footer={{
          primaryAction: {
            label: 'Add Quote',
            onClick: handleAddQuote,
            disabled: !newQuote.text.trim(),
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: () => setShowAddModal(false),
          },
        }}
      >
        <ModalSection spacing="normal">
          <TextInput
            label="Quote *"
            value={newQuote.text}
            onChange={(val) => setNewQuote(prev => ({ ...prev, text: val }))}
            placeholder="Enter the quote..."
            multiline
            rows={4}
            autoResize
            variant="default"
          />

          <div style={{ marginTop: 16 }}>
            <TextInput
              label="Author"
              value={newQuote.author}
              onChange={(val) => setNewQuote(prev => ({ ...prev, author: val }))}
              placeholder="Who said this? (optional)"
              variant="default"
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <TextInput
              label="Show on specific date"
              type="text"
              value={newQuote.showOnDate}
              onChange={(val) => setNewQuote(prev => ({ ...prev, showOnDate: val }))}
              placeholder="YYYY-MM-DD"
              helperText="Perfect for birthdays or anniversaries!"
              variant="default"
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--kff-data-font)',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--ion-color-medium)',
              marginBottom: 8,
            }}>
              Tags
            </label>

            {/* Existing tags */}
            {newQuote.tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {newQuote.tags.map(tag => (
                  <Badge
                    key={tag}
                    variant="primary"
                    size="md"
                    onDismiss={() => removeTag(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Add new tag */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  value={newTag}
                  onChange={setNewTag}
                  placeholder="Add tag..."
                  variant="default"
                  onKeyDown={(e) => e.key === 'Enter' && addTagHandler()}
                />
              </div>
              <Button
                fill="clear"
                size="default"
                iconStart={add}
                onClick={addTagHandler}
                aria-label="Add tag"
              />
            </div>
          </div>
        </ModalSection>
      </Modal>
    </PageShell>
  );
};

export default QuoteManager;
